import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Star } from 'lucide-react';
import { publicAPI } from '../services/apiService';

const ReviewModal = ({ isOpen, onClose, onReviewSubmitted }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 0,
    service: '',
    text_de: '',
    text_ru: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);

  // Load services from API
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoadingServices(true);
      const response = await fetch(`${window.location.origin}/api/admin/settings/services`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Transform API data to the format expected by the form
        const transformedServices = data.services
          .filter(service => service.active) // Only show active services
          .map(service => ({
            value: service.id,
            label_de: service.name_de,
            label_ru: service.name_ru
          }));
        
        setServices(transformedServices);
      } else {
        throw new Error('Failed to fetch services');
      }
    } catch (error) {
      console.error('Error loading services:', error);
      // Fallback to static services if API fails
      setServices([
        { value: 'wedding', label_de: 'Hochzeitsfotografie', label_ru: 'Свадебная фотография' },
        { value: 'portrait', label_de: 'Portraitfotografie', label_ru: 'Портретная фотография' },
        { value: 'architecture', label_de: 'Architekturfotografie', label_ru: 'Архитектурная фотография' },
        { value: 'events', label_de: 'Eventfotografie', label_ru: 'Событийная фотография' },
        { value: 'restoration', label_de: 'Foto-Restauration & Kolorierung', label_ru: 'Реставрация и колоризация фотографий' },
        { value: 'ai-photoshoot', label_de: 'KI-Virtuelle Fotosessions', label_ru: 'Виртуальные фотосессии с ИИ' }
      ]);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRatingChange = (rating) => {
    setFormData({
      ...formData,
      rating
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit review to backend API
      const response = await fetch(`${window.location.origin}/api/public/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit review');
      }

      setSubmitted(true);
      
      // Notify parent component to refresh reviews
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      
      // Reset form after 3 seconds and close modal
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          rating: 0,
          service: '',
          text_de: '',
          text_ru: ''
        });
        setSubmitted(false);
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Fehler beim Senden des Bewertung. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        email: '',
        rating: 0,
        service: '',
        text_de: '',
        text_ru: ''
      });
      setSubmitted(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: '"Times New Roman", serif' }}>
            {currentLang === 'de' ? 'Bewertung Hinterlassen' : 'Оставить отзыв'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors duration-200"
            disabled={isSubmitting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="mb-4">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-900">
                  <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {currentLang === 'de' ? 'Vielen Dank!' : 'Спасибо!'}
              </h3>
              <p className="text-gray-300">
                {currentLang === 'de' 
                  ? 'Ihre Bewertung wurde erfolgreich gesendet und wird bald veröffentlicht.' 
                  : 'Ваш отзыв был успешно отправлен и скоро будет опубликован.'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                    {currentLang === 'de' ? 'Name' : 'Имя'} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={currentLang === 'de' ? 'Ihr Name' : 'Ваше имя'}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ihre.email@beispiel.de"
                  />
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {currentLang === 'de' ? 'Bewertung' : 'Рейтинг'} *
                </label>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingChange(star)}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`h-8 w-8 ${
                          star <= formData.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-500'
                        } hover:text-yellow-400 transition-colors duration-200`}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-400">
                    {formData.rating > 0 && `${formData.rating}/5`}
                  </span>
                </div>
              </div>

              {/* Service */}
              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-1">
                  {currentLang === 'de' ? 'Service' : 'Услуга'} *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  disabled={loadingServices}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                >
                  <option value="" className="text-gray-400">
                    {loadingServices 
                      ? (currentLang === 'de' ? 'Services laden...' : 'Загрузка услуг...') 
                      : (currentLang === 'de' ? 'Service auswählen...' : 'Выберите услугу...')}
                  </option>
                  {!loadingServices && services.map((service) => (
                    <option key={service.value} value={service.value} className="text-white">
                      {service[`label_${currentLang}`]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Review Text */}
              <div>
                <label htmlFor="review_text" className="block text-sm font-medium text-gray-300 mb-1">
                  {currentLang === 'de' ? 'Ihre Bewertung' : 'Ваш отзыв'} *
                </label>
                <textarea
                  id="review_text"
                  name={currentLang === 'de' ? 'text_de' : 'text_ru'}
                  required
                  rows={4}
                  value={formData[currentLang === 'de' ? 'text_de' : 'text_ru']}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder={currentLang === 'de' 
                    ? 'Beschreiben Sie Ihre Erfahrung mit dem Service...' 
                    : 'Опишите ваш опыт работы с сервисом...'}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors duration-200 disabled:opacity-50"
                >
                  {currentLang === 'de' ? 'Abbrechen' : 'Отмена'}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || formData.rating === 0}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isSubmitting 
                    ? (currentLang === 'de' ? 'Wird gesendet...' : 'Отправляется...') 
                    : (currentLang === 'de' ? 'Bewertung Senden' : 'Отправить отзыв')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;